import { FC, useMemo, useState } from 'react';
import cx from 'classnames';
import toast from 'react-hot-toast';
import styles from '../Step3.module.scss';
import useIsLoadedInsideIframe from 'hooks/useIsLoadedInsideIframe';
import PatientCard from '../PatientCard/PatientCard';
import { useAppDispatch, useAppSelector } from 'redux/reduxHooks';
import NavigationBtnControl from 'components/BookingForm/NavigationBtnControl/NavigationBtnControl';
import {
  popStep,
  replaceStep,
  STEPS,
  updateIsKickedFromStep3,
  updateStep3,
} from 'redux/bookingFormSlice';
import { ReactComponent as ErrorIcon } from 'assets/icons/error.svg';
import { DentistInfo } from 'pages/UpdatedSearchResultPage/utils';
import { ChairOptimization } from 'pages/ClinicDetailsPage/ClinicDetailsPage';
import { formatDobForBE } from 'components/BookingForm/utils/helperFunction';
import { AUTH_SYS_TYPE } from 'utils/constants/common';
import { signUpSocialThunk, signUpThunk } from 'redux/authSlice';
import { useLocation, useParams } from 'react-router-dom';
import {
  convertTimeHHmmss,
  MINUTE_OF_A_BLOCK,
} from 'utils/convertMinutesIntoHoursOfDay';
import { loadOtpToken } from 'utils/storage';
import { parseTrafficSource } from 'utils/parseQueryUrl';
import { createBookingAPI } from 'services/APIs';
import moment from 'moment';
import { ROUTES } from 'routes/config';
import { batch } from 'react-redux';
import {
  ERROR_CODE,
  INSURANCE_PAY_FOR_MY_SELF,
} from 'components/DentalMap/utils/constant';
import { updatePractitionerExamCleaningServiceId } from 'redux/practitionerDetailsSlice';
import { updateExamCleaningServiceId } from 'redux/clinicDetailsSlice';
import { renderToast } from 'components/CustomToast/CustomToast';
import forwardEvent, { EVENT } from 'utils/tracking/forwardEventTracking';
import { scheduleTracking } from 'utils/tracking/metaEventTracking';
import sendTracking from 'utils/tracking/forwardTiktokOrGtagTracking';
import { useHistory } from 'react-router-dom';

import InsuranceSection from '../InsuranceSection/InsuranceSection';
import { useStep3Navigation } from 'components/BookingForm/Step3/useStep3Navigation';
import { useChargeFirstBooking } from 'components/BookingForm/Step3/useChargeFirstBooking';
import { setShowInsuranceModal } from 'redux/clinicDetailsSlice';
import { updateSelectedLastInsurance } from 'redux/bookingFormSlice';
import { checkExistingPractitionerAcceptInsurance } from 'utils/checkExistingPractitionerAcceptInsurance';
import { getIsPatientNoteRequired } from 'utils/getIsPatientNoteRequired';
import { Asterix } from 'components/Form/HelperText/HelperText';

interface WithoutHomeAddressProps {
  onClose: () => void;
  clinicId: string;
  practitionerId: string;
  date: string;
  serviceId: string;
  operatoryId?: string;
  timeBlock: number;
  dentist: DentistInfo;
  chairOptimizations?: ChairOptimization[];
  isPreferredDoctor: boolean;
}

const WithoutHomeAddress: FC<WithoutHomeAddressProps> = ({
  onClose,
  clinicId,
  practitionerId,
  date,
  timeBlock,
  operatoryId,
  serviceId,
  dentist,
  chairOptimizations,
  isPreferredDoctor,
}) => {
  const isLoadedInsideIframe = useIsLoadedInsideIframe();
  const [isLoading, setIsLoading] = useState(false);
  const history = useHistory();

  const {
    bookingFormSlice: {
      step1,
      step2: { signUp },
      step3: {
        patientInfo,
        currentInsurance,
        isPayForMyself,
        insurancePaymentMethod,
        insurancesByAvailableDoctors,
      },
      isKickedFromStep3,
      bookingInformation,
    },
    authSlice: { isLoggedIn, sysType, tempAccessToken },
    clinicDetailsSlice,
  } = useAppSelector((state) => state);
  const dispatch = useAppDispatch();
  const { routeAfterStep } = useStep3Navigation();
  const { finalizeChargeFirstBooking } = useChargeFirstBooking();
  const isChargeFirst = clinicDetailsSlice.clinicData.isStripeCheckoutEnabled;

  const params = useParams<{
    practitionerSlugName?: string;
  }>();

  const isPractitionerPage = params.practitionerSlugName;

  const { search, pathname } = useLocation();

  const searchParams = new URLSearchParams(search);

  const patient = patientInfo!;

  const isCharacterExceed = patient.note.length! > 255;

  const isPatientNoteRequired = getIsPatientNoteRequired(
    clinicDetailsSlice.clinicData.services,
    serviceId
  );

  const isPatientNoteMissing = isPatientNoteRequired && !patient.note.trim();

  const containerClassName = cx({
    [styles['container']]: true,
    [styles['container-iframe']]: isLoadedInsideIframe,
  });

  const noteLengthClassName = cx({
    [styles['note-length']]: true,
    [styles['note-length-error']]: isCharacterExceed,
  });

  const textareaClassName = cx({
    [styles['textarea']]: true,
    [styles['textarea-error']]: isCharacterExceed,
  });

  const headerSectionClassName = cx({
    [styles['headerSection']]: true,
    [styles['headerSection-iframe']]: isLoadedInsideIframe,
  });

  const bodySectionClassName = cx({
    [styles['bodySection']]: true,
    [styles['bodySection-iframe']]: isLoadedInsideIframe,
  });

  const onNoteChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    dispatch(
      updateStep3({ patientInfo: { ...patientInfo!, note: e.target.value } })
    );
  };

  const createUser = async () => {
    const isSignUp = signUp.email;

    if (isKickedFromStep3) {
      return tempAccessToken;
    }

    if (!isSignUp || isLoggedIn) {
      return;
    }

    const user = {
      email: patient.email,
      firstName: patient.firstName,
      lastName: patient.lastName,
      dob: formatDobForBE(patient.dob),
      phoneNumber: patient.phoneNumber,
    };

    if (sysType !== AUTH_SYS_TYPE.PASSWORD) {
      await dispatch(signUpSocialThunk(user));
      return;
    }

    const rs = await dispatch(
      signUpThunk({
        ...user,
        password: signUp.password,
        sysType: AUTH_SYS_TYPE.PASSWORD,
      })
    );

    return (rs.payload as any).accessToken;
  };

  const buildBookingPayload = (patientInfo: {
    id?: string;
    firstName: string;
    lastName: string;
    dob: string;
    email: string;
    phoneNumber: string;
    isNewPatient: boolean;
  }) => {
    const { id, firstName, lastName, email, phoneNumber, dob } = patientInfo;

    if (!operatoryId) {
      throw new Error('Something went wrong. Please try again later');
    }

    const code = searchParams.get('code');
    const source = parseTrafficSource(search);

    const isGoogleBookOnline = searchParams.get('rwg_token');

    const payload = {
      isClinicBooking: false,
      clinicId,
      patients: [
        {
          id,
          firstName,
          lastName,
          dob,
          email,
          phoneNumber,
          operatoryId: operatoryId || '',
          serviceId: serviceId,
          doctorId: practitionerId,
          appointmentDate: moment(date).format('YYYY-MM-DD'),
          startTime: convertTimeHHmmss(timeBlock * MINUTE_OF_A_BLOCK),
          useInsurance: false,
          isPreferredDoctor: Boolean(isPreferredDoctor),
          ...(chairOptimizations && { chairOptimizations }),
          dentist,
          patientNote: patient.note,
        },
      ],
      isSignedIn: Boolean(isLoggedIn),
      ...(source && { source }),
      ...(code && { code }),
      ...(currentInsurance && {
        clinicInsuranceId: currentInsurance.id,
        insuranceName: currentInsurance.name,
      }),
      ...(isGoogleBookOnline && { isGoogleBookOnline: !!isGoogleBookOnline }),
      ...(isPayForMyself && {
        insuranceName: INSURANCE_PAY_FOR_MY_SELF,
        insurancePaymentMethod: insurancePaymentMethod?.id || null,
      }),
    };

    return payload;
  };

  const createBooking = async (
    patientInfo: {
      id?: string;
      firstName: string;
      lastName: string;
      dob: string;
      email: string;
      phoneNumber: string;
      isNewPatient: boolean;
    },
    tempAccessToken?: string
  ) => {
    const payload = buildBookingPayload(patientInfo);
    const data = await createBookingAPI(payload, {
      otpToken: loadOtpToken(step1.phoneNumber),
      tempAccessToken,
    });

    return data;
  };

  const fireSubmitTracking = () => {
    if (pathname !== ROUTES.RESULT_PAGE) {
      scheduleTracking(clinicDetailsSlice.clinicData.metaPixelCode);
      sendTracking(clinicDetailsSlice.clinicData.customScript);
      forwardEvent({
        googleTagManagerId: clinicDetailsSlice.clinicData.googleTagManagerId,
        isLoadedInsideIframe,
        website: clinicDetailsSlice.clinicData.website,
        payload: { event: EVENT.FORM_SUBMITTED },
      });
      forwardEvent({
        googleTagManagerId: clinicDetailsSlice.clinicData.googleTagManagerId,
        isLoadedInsideIframe,
        website: clinicDetailsSlice.clinicData.website,
        payload: { event: EVENT.FINAL_SLOT_SELECTED },
      });
    }
  };

  const onSubmit = async () => {
    try {
      setIsLoading(true);
      const tempAccessToken = await createUser();

      if (isChargeFirst) {
        const payload = buildBookingPayload(patient);
        dispatch(
          updateStep3({ pendingBookingPayload: payload, tempAccessToken })
        );
        fireSubmitTracking();

        await finalizeChargeFirstBooking(payload);
        setIsLoading(false);
        return;
      }

      const response = await createBooking(patient, tempAccessToken);

      dispatch(
        updateStep3({
          bookingId: response.id,
          tempAccessToken,
          clientSecret: response.clientSecret,
        })
      );

      fireSubmitTracking();
      setIsLoading(false);
      routeAfterStep(STEPS.FINAL, response.clientSecret);
    } catch (error: any) {
      let message = 'Something went wrong. Please try again later';
      if (error === 'Unauthorized') {
        return batch(() => {
          dispatch(replaceStep(STEPS.VERIFICATION));
        });
      }
      if (error?.response?.status === 400) {
        message = error.response.data.message;
      }
      if (
        error?.response?.status === 400 &&
        error?.response?.data.code === ERROR_CODE.KICK_BACK
      ) {
        const newServiceId = error?.response.data.serviceId;
        const shortLinkCode = error?.response.data.shortLink;
        const serviceSlugName = error?.response.data.serviceSlugName;

        batch(() => {
          dispatch(updateIsKickedFromStep3());
          isPractitionerPage
            ? dispatch(updatePractitionerExamCleaningServiceId(newServiceId))
            : dispatch(updateExamCleaningServiceId(newServiceId));
          renderToast({
            type: 'warning',
            message:
              'Unfortunately, there are no available slots matching your criteria. Please select a different time slot.',
          });
        });
        history.replace({
          search: `?code=${shortLinkCode}&service=${serviceSlugName}`,
        });
        onClose();
        return;
      }
      renderToast({ message });
      setIsLoading(false);
    }
  };

  const onBackClick = () => {
    if (isKickedFromStep3) {
      return onClose();
    }
    dispatch(updateStep3({ currentHomeAddress: '' }));
    dispatch(popStep());
  };

  const isInsuranceValid = clinicDetailsSlice.clinicData.isInsuranceEnabled
    ? (isPayForMyself && insurancePaymentMethod !== null) ||
      (currentInsurance !== null && currentInsurance.isActive)
    : true;

  const isExistingPractitionerAcceptInsurance = useMemo(() => {
    if (clinicDetailsSlice.clinicData.isInsuranceEnabled) {
      const result = checkExistingPractitionerAcceptInsurance({
        doctors: insurancesByAvailableDoctors,
        practitionerId,
        currentInsuranceCarrierId: currentInsurance?.insuranceId ?? null,
        isPayForMyself,
        isPreferredDoctor: isPreferredDoctor ?? false,
        isExamCleaningService:
          bookingInformation?.isExamCleaningService || false,
        dentistId: bookingInformation?.dentist?.id || undefined,
      });

      return result.isAcceptedInsurance;
    }

    return true;
  }, [
    insurancesByAvailableDoctors,
    practitionerId,
    currentInsurance,
    isPayForMyself,
    isPreferredDoctor,
    bookingInformation,
    clinicDetailsSlice.clinicData.isInsuranceEnabled,
  ]);

  return (
    <div className={containerClassName}>
      <div className={styles['card-container']}>
        <h3>Patient Info</h3>
        <PatientCard
          patient={{
            id: '',
            dob: patientInfo!.dob,
            email: patientInfo!.email,
            firstName: patientInfo!.firstName,
            lastName: patientInfo!.lastName,
            phoneNumber: patientInfo!.phoneNumber,
          }}
          chosenPatientId={''}
        />

        <InsuranceSection
          clinicId={clinicId}
          isPreferredDoctor={isPreferredDoctor}
          onFindOtherPractitioners={() => {
            dispatch(
              updateSelectedLastInsurance(
                currentInsurance
                  ? {
                      insuranceCarrierName: currentInsurance.name,
                      insuranceCarrierId: currentInsurance.insuranceId,
                      clinicInsuranceId: currentInsurance.id,
                    }
                  : null
              )
            );
            onClose();
            renderToast({
              type: 'warning',
              duration: 8000,
              message: (
                <span
                  onClick={() => {
                    toast.dismiss();
                    dispatch(setShowInsuranceModal(true));
                  }}
                  style={{
                    cursor: 'pointer',
                    textDecoration: 'underline',
                    fontWeight: 600,
                  }}
                >
                  Choose another insurance
                </span>
              ),
            });
          }}
        />

        <section className={styles['section']}>
          <div className={headerSectionClassName}>
            <div className={styles['title']}>
              <span>Additional Information</span>
            </div>
          </div>
          <div className={bodySectionClassName}>
            <h3 className={styles['section-title']}>
              Anything else we should know?
              {isPatientNoteRequired && (
                <>
                  {' '}
                  <Asterix error={'required'} />
                </>
              )}
            </h3>
            <textarea
              className={textareaClassName}
              placeholder="Ie. area of concern/pain, insurance info, requested days/times to come sooner if opening available, etc."
              value={patientInfo?.note ?? ''}
              onChange={onNoteChange}
            />
            <p className={noteLengthClassName}>
              {isCharacterExceed && <ErrorIcon />}
              <span>{patientInfo?.note.length ?? 0}/255 characters</span>
            </p>
          </div>
        </section>
      </div>

      <NavigationBtnControl
        data-firstin-submit={true}
        onNext={onSubmit}
        onBack={onBackClick}
        nextBtnTitle={isChargeFirst ? 'Next' : 'Submit'}
        isLoading={isLoading}
        isDisabledNextBtn={
          isCharacterExceed ||
          isPatientNoteMissing ||
          !isInsuranceValid ||
          !isExistingPractitionerAcceptInsurance
        }
      />
    </div>
  );
};

export default WithoutHomeAddress;
